/**
 * Shared domain schemas and types for Kandoo.
 *
 * These Zod schemas are the single source of truth for entity shapes and API
 * request/response payloads, used by both the backend (validation) and the
 * frontend (typed API client).
 */
import { z } from "zod";
/* -------------------------------------------------------------------------- */
/* Primitives                                                                  */
/* -------------------------------------------------------------------------- */
export const uuidSchema = z.string().uuid();
export const isoTimestampSchema = z.string().datetime({ offset: true });
export const tokenSecretSchema = z
    .string()
    .regex(/^kdo_[A-Za-z0-9_-]{32,}$/u, "expected a kdo_… API token");
/** Fixed palette of label / cover colors (keys mapped to CSS on the client). */
export const labelColorSchema = z.enum([
    "green",
    "lime",
    "yellow",
    "orange",
    "red",
    "pink",
    "purple",
    "blue",
    "sky",
    "gray",
]);
/* -------------------------------------------------------------------------- */
/* Entities                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Built-in avatar presets — hand-drawn SVG art a user can pick instead of the
 * default identicon. Order here is the order shown in the picker.
 */
export const AVATAR_PRESETS = [
    "duck",
    "cow",
    "pig",
    "frog",
    "goose",
    "cheese",
    "space",
    "robot",
    "cat",
    "fox",
    "owl",
    "mushroom",
    "turtle",
    "donut",
    "sushi",
    "avocado",
    "strawberry",
    "coffee",
    "boba",
    "beer",
    "earth",
    "mars",
    "moon",
];
/**
 * Upper bound on a stored avatar string. A user-uploaded image is downscaled to
 * ~128px client-side, so its base64 data URI is well under this; the cap just
 * stops an oversized or hand-crafted value from being persisted.
 */
export const AVATAR_MAX_LENGTH = 300_000;
const AVATAR_IMAGE_RE = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/;
/**
 * Upper bound on a card title. Titles are the whole card for a lot of people —
 * a one-line request typed straight into the composer — so this is deliberately
 * roomy; the detail that doesn't fit belongs in the description.
 */
export const CARD_TITLE_MAX_LENGTH = 400;
/**
 * Stored avatar value. `null` falls back to the deterministic identicon; a
 * `preset:<key>` string selects a built-in drawing; a `data:image/…` URI is a
 * user-uploaded image (downscaled client-side before it's sent).
 */
export const avatarValueSchema = z
    .string()
    .max(AVATAR_MAX_LENGTH)
    .refine((v) => AVATAR_PRESETS.some((p) => v === `preset:${p}`) || AVATAR_IMAGE_RE.test(v), "invalid avatar")
    .nullable();
/** True for a stored avatar value that's a user-uploaded image. */
export function isImageAvatar(value) {
    return !!value && value.startsWith("data:image/");
}
export const userSchema = z.object({
    id: uuidSchema,
    username: z.string().min(3).max(32),
    displayName: z.string().nullable(),
    avatar: avatarValueSchema,
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
});
/** Access role a user holds on a board (or org). owner > editor > viewer. */
export const boardRoleSchema = z.enum(["owner", "editor", "viewer"]);
/**
 * Board background. `value` is a CSS color for "color", a CSS gradient string
 * for "gradient", or a data URI (uploaded PNG/SVG) for "image". Capped to keep
 * uploaded images to a sane inline size.
 */
export const boardBackgroundSchema = z.object({
    type: z.enum(["color", "gradient", "image"]),
    value: z.string().min(1).max(3_000_000),
    /** Optional overlay pattern id (see the frontend's PATTERN_PRESETS). */
    pattern: z.string().max(40).optional(),
});
export const boardSchema = z.object({
    id: uuidSchema,
    ownerId: uuidSchema,
    orgId: uuidSchema.nullable(),
    title: z.string().min(1).max(120),
    description: z.string().nullable(),
    background: boardBackgroundSchema.nullable(),
    /** Show a preview of each card's first image attachment on its board face. */
    cardPreviews: z.boolean().optional(),
    position: z.number().int(),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
    /** Whether the requesting user has starred (pinned) this board. */
    starred: z.boolean().optional(),
});
export const listSchema = z.object({
    id: uuidSchema,
    boardId: uuidSchema,
    title: z.string().min(1).max(120),
    position: z.number(),
    color: labelColorSchema.nullable(),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
});
export const cardSchema = z.object({
    id: uuidSchema,
    listId: uuidSchema,
    title: z.string().min(1).max(CARD_TITLE_MAX_LENGTH),
    description: z.string().nullable(),
    position: z.number(),
    dueDate: isoTimestampSchema.nullable(),
    completed: z.boolean(),
    coverColor: labelColorSchema.nullable(),
    archivedAt: isoTimestampSchema.nullable(),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
});
export const checklistSchema = z.object({
    id: uuidSchema,
    cardId: uuidSchema,
    title: z.string().min(1).max(120),
    position: z.number().int(),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
});
export const checklistItemSchema = z.object({
    id: uuidSchema,
    checklistId: uuidSchema,
    content: z.string().min(1).max(300),
    checked: z.boolean(),
    position: z.number().int(),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
});
export const labelSchema = z.object({
    id: uuidSchema,
    boardId: uuidSchema,
    name: z.string().max(50).nullable(),
    color: labelColorSchema,
    position: z.number().int(),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
});
export const cardRelationshipSchema = z.enum([
    "subtask",
    "blocked-by",
    "related",
]);
export const cardLinkSchema = z.object({
    id: uuidSchema,
    parentCardId: uuidSchema,
    childCardId: uuidSchema,
    relationship: cardRelationshipSchema,
    createdAt: isoTimestampSchema,
});
/** Board-role ceiling for a token; null/absent = the user's full role. */
export const tokenRoleCapSchema = z.enum(["editor", "viewer"]);
export const apiTokenSchema = z.object({
    id: uuidSchema,
    name: z.string().min(1).max(80),
    prefix: z.string(),
    lastUsedAt: isoTimestampSchema.nullable(),
    expiresAt: isoTimestampSchema.nullable(),
    roleCap: tokenRoleCapSchema.nullable(),
    revoked: z.boolean(),
    createdAt: isoTimestampSchema,
});
/**
 * A service user: a bot identity owned by a real user, acting only via API
 * tokens. It inherits its owner's board access; attribution stays its own.
 */
export const serviceUserSchema = z.object({
    id: uuidSchema,
    username: z.string(),
    displayName: z.string().nullable(),
    avatar: avatarValueSchema,
    createdAt: isoTimestampSchema,
});
/** Owner-editable fields on a service user (bot). */
export const updateServiceUserRequestSchema = z.object({
    avatar: avatarValueSchema.optional(),
});
export const serviceUserWithTokensSchema = serviceUserSchema.extend({
    tokens: z.array(apiTokenSchema),
});
export const createServiceUserRequestSchema = z.object({
    username: z.string().min(3).max(32).regex(/^[A-Za-z0-9_.-]+$/u),
    displayName: z.string().max(80).optional(),
});
/* -------------------------------------------------------------------------- */
/* Auth                                                                        */
/* -------------------------------------------------------------------------- */
export const registerRequestSchema = z.object({
    username: z.string().min(3).max(32).regex(/^[A-Za-z0-9_.-]+$/u),
    password: z.string().min(8).max(128),
    displayName: z.string().max(80).optional(),
});
export const loginRequestSchema = z.object({
    username: z.string().min(3).max(32),
    password: z.string().min(1).max(128),
});
export const authMeResponseSchema = userSchema;
/** Public, unauthenticated server settings the sign-in UI needs up front. */
export const authConfigResponseSchema = z.object({
    registration: z.enum(["open", "closed"]),
});
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(128),
    newPassword: z.string().min(8).max(128),
});
export const updateProfileRequestSchema = z.object({
    displayName: z.string().max(80).nullable().optional(),
    avatar: avatarValueSchema.optional(),
});
/** `GET /api/users/avatars` → userId → stored avatar value (only users who set one). */
export const avatarMapSchema = z.record(uuidSchema, z.string());
/* -------------------------------------------------------------------------- */
/* Tokens                                                                      */
/* -------------------------------------------------------------------------- */
export const createTokenRequestSchema = z.object({
    name: z.string().min(1).max(80),
    expiresAt: isoTimestampSchema.nullable().optional(),
    roleCap: tokenRoleCapSchema.optional(),
});
/** Returned only once at creation time — includes the secret token. */
export const apiTokenWithSecretSchema = apiTokenSchema.extend({
    token: tokenSecretSchema,
});
/* -------------------------------------------------------------------------- */
/* Boards                                                                      */
/* -------------------------------------------------------------------------- */
export const createBoardRequestSchema = z.object({
    title: z.string().min(1).max(120),
    description: z.string().max(10000).optional(),
});
export const updateBoardRequestSchema = z.object({
    title: z.string().min(1).max(120).optional(),
    description: z.string().max(10000).nullable().optional(),
    background: boardBackgroundSchema.nullable().optional(),
    cardPreviews: z.boolean().optional(),
});
/* -------------------------------------------------------------------------- */
/* Organizations & membership                                                  */
/* -------------------------------------------------------------------------- */
export const orgSchema = z.object({
    id: uuidSchema,
    name: z.string().min(1).max(80),
    ownerId: uuidSchema,
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
});
/** Roles that can be granted to an invited member (owner is not assignable). */
export const memberRoleSchema = z.enum(["editor", "viewer"]);
/**
 * Where a board member's access comes from. "board" is an explicit invite,
 * "org" is inherited from the board's organization, and "service" is a service
 * user (bot) inheriting its owner's access. Only "board" rows can be re-roled or
 * removed on the board itself — the others are managed elsewhere.
 */
/**
 * Add a trusted identity to a service user's token: whose @mentions that token
 * should treat as requests. Fail-closed — an empty list trusts nobody.
 */
export const trustIdentityRequestSchema = z.object({
    username: z.string().min(1).max(32),
});
export const trustedIdentitySchema = z.object({
    userId: uuidSchema,
    username: z.string(),
    displayName: z.string().nullable(),
    createdAt: z.string(),
});
export const memberSourceSchema = z.enum(["owner", "board", "org", "service"]);
/** A member row as returned to clients (org or board). */
export const memberSchema = z.object({
    userId: uuidSchema,
    username: z.string(),
    displayName: z.string().nullable(),
    role: boardRoleSchema,
    isOwner: z.boolean(),
    /** Absent on org member lists, where every row is by definition from the org. */
    source: memberSourceSchema.optional(),
});
export const attachmentSchema = z.object({
    id: uuidSchema,
    cardId: uuidSchema,
    filename: z.string(),
    mime: z.string(),
    size: z.number().int(),
    uploadedBy: uuidSchema.nullable(),
    createdAt: isoTimestampSchema,
});
/** A user assigned to a card (minimal identity for avatars). */
export const cardAssigneeSchema = z.object({
    userId: uuidSchema,
    username: z.string(),
    displayName: z.string().nullable(),
});
export const createOrgRequestSchema = z.object({
    name: z.string().min(1).max(80),
});
export const inviteMemberRequestSchema = z.object({
    username: z.string().min(1).max(32),
    role: memberRoleSchema.optional(),
});
export const updateMemberRoleRequestSchema = z.object({
    role: memberRoleSchema,
});
export const assignBoardOrgRequestSchema = z.object({
    orgId: uuidSchema.nullable(),
});
/* -------------------------------------------------------------------------- */
/* Lists                                                                       */
/* -------------------------------------------------------------------------- */
export const createListRequestSchema = z.object({
    title: z.string().min(1).max(120),
});
export const updateListRequestSchema = z.object({
    title: z.string().min(1).max(120).optional(),
    position: z.number().optional(),
    color: labelColorSchema.nullable().optional(),
});
export const reorderListRequestSchema = z.object({
    position: z.number(),
});
/** Target for "move all cards" — the list the whole column empties into. */
export const moveListCardsRequestSchema = z.object({
    listId: uuidSchema,
});
/** Result of a bulk list operation: how many cards it touched. */
export const bulkCardsResultSchema = z.object({
    count: z.number().int(),
});
/* -------------------------------------------------------------------------- */
/* Cards                                                                       */
/* -------------------------------------------------------------------------- */
export const createCardRequestSchema = z.object({
    title: z.string().min(1).max(CARD_TITLE_MAX_LENGTH),
    description: z.string().max(10000).optional(),
    /** Omit to append the card at the end of the list. */
    position: z.number().optional(),
});
export const updateCardRequestSchema = z.object({
    title: z.string().min(1).max(CARD_TITLE_MAX_LENGTH).optional(),
    description: z.string().max(10000).nullable().optional(),
    dueDate: isoTimestampSchema.nullable().optional(),
    completed: z.boolean().optional(),
    coverColor: labelColorSchema.nullable().optional(),
});
export const moveCardRequestSchema = z.object({
    listId: uuidSchema,
    /** Omit to append the card at the end of the target list. */
    position: z.number().optional(),
});
/* -------------------------------------------------------------------------- */
/* Labels                                                                      */
/* -------------------------------------------------------------------------- */
export const createLabelRequestSchema = z.object({
    color: labelColorSchema,
    name: z.string().max(50).optional(),
});
export const updateLabelRequestSchema = z.object({
    color: labelColorSchema.optional(),
    name: z.string().max(50).nullable().optional(),
    position: z.number().int().optional(),
});
/* -------------------------------------------------------------------------- */
/* Custom fields                                                               */
/* -------------------------------------------------------------------------- */
export const fieldTypeSchema = z.enum([
    "text",
    "number",
    "date",
    "checkbox",
    "select",
    "url",
]);
/** One choice of a select field. */
export const fieldOptionSchema = z.object({
    id: z.string().min(1).max(40),
    name: z.string().trim().min(1).max(50),
    color: labelColorSchema,
});
export const boardFieldSchema = z.object({
    id: uuidSchema,
    boardId: uuidSchema,
    name: z.string().min(1).max(50),
    type: fieldTypeSchema,
    /** Choices for a select field; empty for every other type. */
    options: z.array(fieldOptionSchema),
    position: z.number().int(),
    /** Show this field's value as a pill on the board's card faces. */
    showOnCard: z.boolean(),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
});
/**
 * A stored value: text/url → string, number → number, checkbox → boolean,
 * date → "YYYY-MM-DD", select → the chosen option's id.
 */
export const fieldValueSchema = z.union([
    z.string().max(2000),
    z.number().finite(),
    z.boolean(),
]);
/** Options may omit an id when created; the server assigns one. */
const fieldOptionInputSchema = fieldOptionSchema.extend({
    id: z.string().min(1).max(40).optional(),
});
export const createFieldRequestSchema = z.object({
    name: z.string().trim().min(1).max(50),
    type: fieldTypeSchema,
    options: z.array(fieldOptionInputSchema).max(50).optional(),
    showOnCard: z.boolean().optional(),
});
/** A field's type is fixed once created (stored values depend on it). */
export const updateFieldRequestSchema = z.object({
    name: z.string().trim().min(1).max(50).optional(),
    options: z.array(fieldOptionInputSchema).max(50).optional(),
    showOnCard: z.boolean().optional(),
    position: z.number().int().optional(),
});
export const setFieldValueRequestSchema = z.object({
    /** null clears the value. */
    value: fieldValueSchema.nullable(),
});
/* -------------------------------------------------------------------------- */
/* Checklists                                                                  */
/* -------------------------------------------------------------------------- */
export const createChecklistRequestSchema = z.object({
    title: z.string().min(1).max(120).optional(),
});
export const updateChecklistRequestSchema = z.object({
    title: z.string().min(1).max(120).optional(),
    position: z.number().int().optional(),
});
export const createChecklistItemRequestSchema = z.object({
    content: z.string().min(1).max(300),
});
export const updateChecklistItemRequestSchema = z.object({
    content: z.string().min(1).max(300).optional(),
    checked: z.boolean().optional(),
    position: z.number().int().optional(),
});
/* -------------------------------------------------------------------------- */
/* Card links                                                                  */
/* -------------------------------------------------------------------------- */
export const createCardLinkRequestSchema = z.object({
    childCardId: uuidSchema,
    relationship: cardRelationshipSchema.optional(),
});
/* -------------------------------------------------------------------------- */
/* Comments & activity                                                         */
/* -------------------------------------------------------------------------- */
export const createCommentRequestSchema = z.object({
    body: z.string().min(1).max(5000),
});
export const updateCommentRequestSchema = z.object({
    body: z.string().min(1).max(5000),
});
export const activityTypeSchema = z.enum([
    "created",
    "moved",
    "renamed",
    // Content that can be silently rewritten in place, and so must be attributed:
    // whoever reads a card later needs to know who wrote what they're reading.
    "described",
    "checklist_renamed",
    "checklist_item_edited",
    "completed",
    "uncompleted",
    "due_set",
    "due_cleared",
    "label_added",
    "label_removed",
    "assignee_added",
    "assignee_removed",
    "attachment_added",
    "attachment_removed",
    "archived",
    "unarchived",
    "field_set",
]);
/* -------------------------------------------------------------------------- */
/* API envelope                                                                */
/* -------------------------------------------------------------------------- */
export const errorResponseSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
    }),
});
//# sourceMappingURL=index.js.map